import { DOCUMENT } from '@angular/common';
import {
  Directive,
  effect,
  ElementRef,
  HostListener,
  Inject,
  input,
  Input,
  OnDestroy,
  Renderer2,
  signal,
  computed
} from '@angular/core';
import { type VariantProps, cva } from 'class-variance-authority';
import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { injectHlmTooltipConfig } from './hlm-tooltip.token';

function hlm(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const tooltipVariants = cva(
  'fixed z-[4000] rounded px-2 py-1 text-xs whitespace-nowrap pointer-events-none',
  {
    variants: {
      variant: {
        default: 'bg-black/70 text-white',
        destructive: 'bg-red-500/90 text-white',
        outline: 'bg-white text-gray-900 border border-gray-200',
        secondary: 'bg-gray-100 text-gray-900',
        ghost: 'bg-transparent text-gray-900',
        link: 'bg-transparent text-blue-600 underline-offset-4 hover:underline'
      },
      size: {
        default: 'px-2 py-1 text-xs',
        sm: 'px-1.5 py-0.5 text-xs',
        lg: 'px-3 py-1.5 text-sm'
      }
    },
    defaultVariants: {
      variant: 'destructive',
      size: 'default'
    }
  }
);

export type TooltipVariants = VariantProps<typeof tooltipVariants>;

@Directive({
  selector: '[appTooltip]',
  standalone: true,
  exportAs: 'hlmTooltip'
})
export class TooltipDirective implements OnDestroy {
  private readonly _config = injectHlmTooltipConfig();
  private readonly _additionalClasses = signal<ClassValue>('');

  @Input('appTooltip') tooltipText!: string;
  public readonly userClass = input<ClassValue>('', { alias: 'class' });

  public readonly variant = input<TooltipVariants['variant']>(
    this._config.variant
  );
  public readonly size = input<TooltipVariants['size']>(this._config.size);
  public readonly position = input<'left' | 'right' | 'top' | 'bottom'>(
    this._config.position
  );
  public readonly delay = input<number>(this._config.delay);
  public readonly maxWidth = input<string>(this._config.maxWidth);

  @Input('triggerOnClick') triggerOnClick: boolean = false; // Input to toggle trigger on click

  protected readonly _computedClass = computed(() =>
    hlm(
      tooltipVariants({ variant: this.variant(), size: this.size() }),
      this.userClass(),
      this._additionalClasses()
    )
  );

  private tooltipElement!: HTMLElement | null;
  private hoverTimeoutId!: any;
  private tooltipVisible: boolean = false; // Track if the tooltip is visible

  constructor(
    private el: ElementRef,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private document: Document
  ) {}

  setClass(classes: string): void {
    this._additionalClasses.set(classes);
  }

  isTouchDevice(): boolean {
    return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  }

  // Handle hover with a delay
  @HostListener('mouseenter', ['$event']) onMouseEnter(
    event: MouseEvent
  ): void {
    event.stopPropagation();

    if (!this.tooltipVisible) {
      this.hoverTimeoutId = setTimeout(() => {
        if (!this.tooltipElement) {
          this.showTooltip();
        }
      }, this.delay());
    }
  }

  @HostListener('mouseleave', ['$event']) onMouseLeave(
    event: MouseEvent
  ): void {
    event.stopPropagation();

    // Clear the timeout when mouse leaves before the tooltip is shown
    clearTimeout(this.hoverTimeoutId);

    if (this.tooltipElement) {
      this.hideTooltip();
    }
  }

  // Handle click to toggle tooltip based on the triggerOnClick input
  @HostListener('click', ['$event']) onClick(event: MouseEvent): void {
    if (this.triggerOnClick) {
      event.stopPropagation();
      if (this.tooltipElement) {
        this.hideTooltip();
      } else {
        this.showTooltip();
      }
    }
  }

  ngOnDestroy(): void {
    if (this.tooltipElement) {
      this.hideTooltip();
    }
  }

  private showTooltip(): void {
    if (!this.tooltipText) {
      console.warn('Tooltip text is empty');
      return;
    }

    this.tooltipElement = this.renderer.createElement('span');

    // Apply computed classes
    const classes = this._computedClass();
    this.renderer.setAttribute(this.tooltipElement, 'class', classes);

    // Explicitly set position to fixed to override any conflicting CSS
    this.renderer.setStyle(this.tooltipElement, 'position', 'fixed');

    // Set max width and ensure it doesn't exceed viewport
    const maxWidth = this.maxWidth();
    this.renderer.setStyle(this.tooltipElement, 'max-width', maxWidth);
    this.renderer.setStyle(this.tooltipElement, 'overflow-wrap', 'break-word');
    this.renderer.setStyle(this.tooltipElement, 'white-space', 'normal');

    this.renderer.setProperty(
      this.tooltipElement,
      'textContent',
      this.tooltipText
    );

    this.renderer.appendChild(this.document.body, this.tooltipElement);

    let top, left;
    const padding = 8;
    const elRect = this.el.nativeElement.getBoundingClientRect();
    const tooltipRect = this.tooltipElement.getBoundingClientRect();

    switch (this.position()) {
      case 'right':
        top = elRect.top + elRect.height / 2 - tooltipRect.height / 2;
        left = elRect.right + padding;
        break;
      case 'left':
        top = elRect.top + elRect.height / 2 - tooltipRect.height / 2;
        left = elRect.left - tooltipRect.width - padding;
        break;
      case 'top':
        top = elRect.top - tooltipRect.height - padding;
        left = elRect.left + elRect.width / 2 - tooltipRect.width / 2;
        break;
      case 'bottom':
        top = elRect.bottom + padding;
        left = elRect.left + elRect.width / 2 - tooltipRect.width / 2;
        break;
      default:
        top = elRect.top + elRect.height / 2 - tooltipRect.height / 2;
        left = elRect.right + padding;
    }

    this.renderer.setStyle(this.tooltipElement, 'top', `${top}px`);
    this.renderer.setStyle(this.tooltipElement, 'left', `${left}px`);

    // Add arrow based on position
    this.addArrow(this.position());

    this.tooltipVisible = true; // Mark tooltip as visible
  }

  private addArrow(position: 'left' | 'right' | 'top' | 'bottom'): void {
    if (!this.tooltipElement) return;

    // Create arrow element
    const arrow = this.renderer.createElement('div');
    this.renderer.addClass(arrow, 'absolute');
    this.renderer.addClass(arrow, 'w-0');
    this.renderer.addClass(arrow, 'h-0');
    this.renderer.addClass(arrow, 'border-5');

    // Get the actual computed background color from the tooltip element
    const computedStyle = window.getComputedStyle(this.tooltipElement);
    const backgroundColor = computedStyle.backgroundColor;
    
    // If no background color or transparent, use default
    const arrowColor = backgroundColor && backgroundColor !== 'rgba(0, 0, 0, 0)' && backgroundColor !== 'transparent' 
      ? backgroundColor 
      : 'rgba(0, 0, 0, 0.7)';

    // Position arrow based on tooltip position
    switch (position) {
      case 'right':
        this.renderer.addClass(arrow, '-left-2.5');
        this.renderer.addClass(arrow, 'top-1/2');
        this.renderer.addClass(arrow, '-translate-y-1/2');
        this.renderer.setStyle(arrow, 'border-top', `5px solid transparent`);
        this.renderer.setStyle(arrow, 'border-bottom', `5px solid transparent`);
        this.renderer.setStyle(arrow, 'border-right', `5px solid ${arrowColor}`);
        break;
      case 'left':
        this.renderer.addClass(arrow, '-right-2.5');
        this.renderer.addClass(arrow, 'top-1/2');
        this.renderer.addClass(arrow, '-translate-y-1/2');
        this.renderer.setStyle(arrow, 'border-top', `5px solid transparent`);
        this.renderer.setStyle(arrow, 'border-bottom', `5px solid transparent`);
        this.renderer.setStyle(arrow, 'border-left', `5px solid ${arrowColor}`);
        break;
      case 'top':
        this.renderer.addClass(arrow, '-bottom-2.5');
        this.renderer.addClass(arrow, 'left-1/2');
        this.renderer.addClass(arrow, '-translate-x-1/2');
        this.renderer.setStyle(arrow, 'border-left', `5px solid transparent`);
        this.renderer.setStyle(arrow, 'border-right', `5px solid transparent`);
        this.renderer.setStyle(arrow, 'border-top', `5px solid ${arrowColor}`);
        break;
      case 'bottom':
        this.renderer.addClass(arrow, '-top-2.5');
        this.renderer.addClass(arrow, 'left-1/2');
        this.renderer.addClass(arrow, '-translate-x-1/2');
        this.renderer.setStyle(arrow, 'border-left', `5px solid transparent`);
        this.renderer.setStyle(arrow, 'border-right', `5px solid transparent`);
        this.renderer.setStyle(arrow, 'border-bottom', `5px solid ${arrowColor}`);
        break;
    }

    this.renderer.appendChild(this.tooltipElement, arrow);
  }

  private hideTooltip(): void {
    this.renderer.removeChild(this.document.body, this.tooltipElement);
    this.tooltipElement = null;
    this.tooltipVisible = false; // Mark tooltip as hidden
  }
}
